import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import api from '@/client/core/api'
import { useSystemStore } from '@/client/store/useSystemStore'
import { TCampaign } from '@/typings/api/campaigns.type'
import {
  TGetMessageHistoryParams,
  TMessageHistoryMessage,
  TPutMessageHistoryParams
} from '@/typings/api/twitter_instantwin_message_history.type'

export type TwitterInstantwinMessageHistoryState = {
  loaded: boolean
  campaign: TCampaign | null
  messages: { id: number; name: string; messageType: string }[]
  histories: TMessageHistoryMessage[]
  total: number
  params: TGetMessageHistoryParams
  error: boolean
}

const createInitialState = (): TwitterInstantwinMessageHistoryState => ({
  loaded: false,
  total: 0,
  campaign: null,
  messages: [],
  histories: [],
  params: {
    campaignId: 0,
    sortBy: '-entryDatetime',
    filterType: null,
    filterMessage: null,
    filterLoseReason: null,
    filterResponder: -1,
    pagingNo: 1,
    limitCount: 25
  },
  error: false
})

export const useTwitterInstantwinMessageHistoryStore = defineStore('twitterInstantwinMessageHistoryStore', () => {
  const systemStore = useSystemStore()
  const initialState = createInitialState()

  const loaded = ref<TwitterInstantwinMessageHistoryState['loaded']>(initialState.loaded)
  const total = ref<TwitterInstantwinMessageHistoryState['total']>(initialState.total)
  const campaign = ref<TwitterInstantwinMessageHistoryState['campaign']>(initialState.campaign)
  const messages = ref<TwitterInstantwinMessageHistoryState['messages']>(initialState.messages)
  const histories = ref<TwitterInstantwinMessageHistoryState['histories']>(initialState.histories)
  const params = ref<TwitterInstantwinMessageHistoryState['params']>(initialState.params)
  const error = ref<TwitterInstantwinMessageHistoryState['error']>(initialState.error)

  const resetMessageHistory = async (): Promise<void> => {
    const nextState = createInitialState()
    loaded.value = nextState.loaded
    total.value = nextState.total
    campaign.value = nextState.campaign
    messages.value = nextState.messages
    histories.value = nextState.histories
    params.value = nextState.params
    error.value = nextState.error
  }

  const fetchCampaign = async (): Promise<void> => {
    const result = await api.get('/campaigns/' + params.value.campaignId)
    if (result.data) {
      campaign.value = result.data
    }
  }

  const fetchHistoryList = async (): Promise<void> => {
    await systemStore.setLoading(true)
    error.value = false
    const result = await api.get('/twitter_instantwin_message_histories', {
      params: params.value
    })
    loaded.value = true
    await systemStore.setLoading(false)
    if (!result.data) {
      error.value = true
      return
    }

    histories.value = result.data.histories
    if (params.value.pagingNo === 1) {
      total.value = result.data.dataCount
    }
  }

  const fetchMessages = async (queryParams: { campaignId: number }): Promise<void> => {
    const result = await api.get('/twitter_instantwin_messages', {
      params: queryParams
    })

    if (!result.data) {
      return
    }

    messages.value = result.data.messages.map(message => ({
      id: message.id,
      name: message.name,
      messageType: message.messageType
    }))
  }

  const setHistoryParams = (newParams: Partial<TGetMessageHistoryParams>): void => {
    params.value = {
      ...params.value,
      ...newParams,
      pagingNo: newParams.pagingNo || 1
    }
  }

  const updateResponder = async (payload: TPutMessageHistoryParams) => {
    const result = await api.put('/twitter_instantwin_applicants/' + payload.applicantId, {
      isResponder: payload.isResponder
    })

    if (result.data) {
      histories.value.forEach(history => {
        if (history.applicant.id === payload.applicantId) {
          history.applicant.isResponder = payload.isResponder
        }
      })

      if (campaign.value && payload.isResponder === 1) {
        campaign.value.responderCount++
      }

      if (campaign.value && payload.isResponder === 0) {
        campaign.value.responderCount--
      }
    }

    if (messages.value.length === 0) {
      await fetchHistoryList()
    }

    return result
  }

  return {
    loaded: readonly(loaded),
    total: readonly(total),
    campaign: readonly(campaign),
    messages: readonly(messages),
    histories: readonly(histories),
    params: readonly(params),
    error: readonly(error),
    resetMessageHistory,
    fetchCampaign,
    fetchHistoryList,
    fetchMessages,
    setHistoryParams,
    updateResponder
  }
})
